import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listLabelSkus,
  listLabelLocations,
  enqueuePrintJobs,
} from "@/lib/operations.functions";
import { listTotes } from "@/lib/admin-config.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { HelpButton } from "@/components/kb/help-button";
import { ContextualTip } from "@/components/kb/contextual-tip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Barcode } from "@/components/barcode";
import { Printer, Tags, Send } from "lucide-react";
import { smallLabelZpl, withCopies } from "@/lib/printing/zpl";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/labels")({ component: LabelsPage });

function LabelsPage() {
  const { tenantId, facilityId, customerId, customers } = useScope();
  const tenantCustomers = useMemo(() => customers.filter((c) => c.tenant_id === tenantId), [customers, tenantId]);
  const fetchSkus = useServerFn(listLabelSkus);
  const fetchLocs = useServerFn(listLabelLocations);
  const enqueueFn = useServerFn(enqueuePrintJobs);

  const [tab, setTab] = useState<"sku" | "location" | "tote">("sku");
  const [search, setSearch] = useState("");
  const [pickedCustomer, setPickedCustomer] = useState<string>(customerId ?? "");
  const [selected, setSelected] = useState<Record<string, { value: string; label: string; sub?: string }>>({});
  const [copies, setCopies] = useState(1);

  const skuCustomer = customerId ?? pickedCustomer;

  const { data: skus = [] } = useQuery({
    queryKey: ["labels-skus", tenantId, skuCustomer],
    enabled: tab === "sku" && !!tenantId && !!skuCustomer,
    queryFn: () => fetchSkus({ data: { tenantId: tenantId!, customerId: skuCustomer } }),
  });

  const { data: locations = [] } = useQuery({
    queryKey: ["labels-locations", tenantId, facilityId],
    enabled: tab === "location" && !!tenantId && !!facilityId,
    queryFn: () => fetchLocs({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });

  const fetchTotes = useServerFn(listTotes);
  const { data: totes = [] } = useQuery({
    queryKey: ["labels-totes", tenantId, facilityId],
    enabled: tab === "tote" && !!tenantId && !!facilityId,
    queryFn: () => fetchTotes({ data: { facilityId: facilityId! } }),
  });

  const filteredSkus = skus.filter((s) =>
    !search || s.sku_code.toLowerCase().includes(search.toLowerCase()) || s.name.toLowerCase().includes(search.toLowerCase()),
  );
  const filteredLocs = locations.filter((l) => !search || l.code.toLowerCase().includes(search.toLowerCase()));
  const filteredTotes = (totes as Array<{ id: string; code: string; status?: string | null }>)
    .filter((t) => !search || t.code.toLowerCase().includes(search.toLowerCase()));

  const toggle = (key: string, item: { value: string; label: string; sub?: string }) => {
    setSelected((s) => {
      const next = { ...s };
      if (next[key]) delete next[key];
      else next[key] = item;
      return next;
    });
  };

  const labelsToPrint = useMemo(() => {
    const arr: { value: string; label: string; sub?: string }[] = [];
    for (const item of Object.values(selected)) {
      for (let i = 0; i < copies; i++) arr.push(item);
    }
    return arr;
  }, [selected, copies]);

  const sendToZplQueue = async () => {
    if (!tenantId || labelsToPrint.length === 0) return;
    const items = Object.values(selected);
    const kind: "sku_label" | "location_label" = tab === "sku" ? "sku_label" : "location_label";
    const jobs = items.map((item) => ({
      payload: withCopies(
        smallLabelZpl({ code: item.label, barcode: item.value, subtitle: item.sub }),
        copies,
      ),
    }));
    try {
      const res = await runServerFn(
        () =>
          enqueueFn({
            data: {
              tenantId,
              facilityId: facilityId ?? null,
              kind,
              copies,
              jobs,
            },
          }),
        "Queue ZPL",
      );
      toast.success(`Queued ${res.queued} job${res.queued === 1 ? "" : "s"} to ZPL printer queue`);
    } catch {
      // runServerFn already surfaced toast
    }
  };

  return (
    <>
      <PageHeader
        title="Labels"
        description="Print barcode labels for SKUs, locations, and totes."
        actions={
          <div className="flex items-center gap-2">
            <div className="text-sm text-muted-foreground">{Object.keys(selected).length} selected · {labelsToPrint.length} labels</div>
            <HelpButton moduleKey="shipping.labels" />
            <Button variant="outline" onClick={sendToZplQueue} disabled={labelsToPrint.length === 0}>
              <Send className="h-4 w-4 mr-2" />Queue ZPL
            </Button>
            <Button onClick={() => window.print()} disabled={labelsToPrint.length === 0}>
              <Printer className="h-4 w-4 mr-2" />Print
            </Button>
          </div>
        }
      />

      {/* On-screen controls — hidden when printing */}
      <div className="p-4 sm:p-6 space-y-4 print:hidden">
        <ContextualTip tipKey="labels_intro" />
        <Tabs value={tab} onValueChange={(v) => { const nt = v as "sku" | "location" | "tote"; setTab(nt); setSelected({}); setCopies((c) => Math.min(c, nt === "sku" ? 30 : 8)); }}>
          <TabsList>
            <TabsTrigger value="sku">SKUs</TabsTrigger>
            <TabsTrigger value="location">Locations</TabsTrigger>
            <TabsTrigger value="tote">Totes</TabsTrigger>
          </TabsList>

          <div className="flex flex-wrap items-end gap-3 mt-4">
            <div className="space-y-2 flex-1 min-w-48">
              <Label>Search</Label>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter…" />
            </div>
            {tab === "sku" && !customerId && (
              <div className="space-y-2 w-64">
                <Label>Customer</Label>
                <Select value={pickedCustomer} onValueChange={setPickedCustomer}>
                  <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>{tenantCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2 w-32">
              <Label>Copies each</Label>
              <Input
                type="number"
                min={1}
                max={tab === "sku" ? 30 : 8}
                value={copies}
                onChange={(e) => {
                  const cap = tab === "sku" ? 30 : 8;
                  setCopies(Math.max(1, Math.min(cap, parseInt(e.target.value || "1", 10))));
                }}
              />
              <p className="text-xs text-muted-foreground">Max {tab === "sku" ? 30 : 8} per sheet</p>
            </div>
          </div>

          <TabsContent value="sku" className="mt-4">
            <div className="border rounded-lg bg-card max-h-[28rem] overflow-auto divide-y">
              {!skuCustomer ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Select a customer to see SKUs.</div>
              ) : filteredSkus.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No SKUs.</div>
              ) : filteredSkus.map((s) => {
                const key = `sku:${s.id}`;
                const checked = !!selected[key];
                return (
                  <label key={key} className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-muted/40">
                    <Checkbox checked={checked} onCheckedChange={() => toggle(key, { value: s.barcode || s.sku_code, label: s.sku_code, sub: s.name })} />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-sm">{s.sku_code}</div>
                      <div className="text-xs text-muted-foreground truncate">{s.name}</div>
                    </div>
                    {s.barcode && <div className="text-xs text-muted-foreground font-mono">{s.barcode}</div>}
                  </label>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="location" className="mt-4">
            <div className="border rounded-lg bg-card max-h-[28rem] overflow-auto divide-y">
              {filteredLocs.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No locations.</div>
              ) : filteredLocs.map((l) => {
                const key = `loc:${l.id}`;
                const checked = !!selected[key];
                return (
                  <label key={key} className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-muted/40">
                    <Checkbox checked={checked} onCheckedChange={() => toggle(key, { value: l.code, label: l.code, sub: l.type })} />
                    <div className="flex-1">
                      <div className="font-mono text-sm">{l.code}</div>
                      <div className="text-xs text-muted-foreground">{l.type}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="tote" className="mt-4">
            <div className="border rounded-lg bg-card max-h-[28rem] overflow-auto divide-y">
              {!facilityId ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Select a facility to see totes.</div>
              ) : filteredTotes.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No totes for this facility. Create totes in Settings → Fulfillment → Totes.</div>
              ) : filteredTotes.map((t) => {
                const key = `tote:${t.id}`;
                const checked = !!selected[key];
                return (
                  <label key={key} className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-muted/40">
                    <Checkbox checked={checked} onCheckedChange={() => toggle(key, { value: t.code, label: t.code, sub: t.status ?? undefined })} />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-sm">{t.code}</div>
                      <div className="text-xs text-muted-foreground truncate">{t.status ?? ""}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>

        {labelsToPrint.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-2 flex items-center gap-2"><Tags className="h-4 w-4" />Preview</h3>
            <div className="text-xs text-muted-foreground mb-2">
              {tab === "sku"
                ? "Format: Uline S-5042 / Avery 5160 — 2⅝\" × 1\", 30 per sheet (3 × 10)"
                : "Format: Uline S-3845 / Avery 5163 — 4\" × 2\", 10 per sheet (2 × 5)"}
            </div>
            <div className="overflow-auto border rounded-md bg-muted/30 p-3">
              <LabelSheet labels={labelsToPrint} kind={tab === "sku" ? "sku" : "location"} compact />
            </div>
          </div>
        )}
      </div>

      {/* Print-only sheet */}
      <div className="hidden print:block print-sheet-root">
        <LabelSheet labels={labelsToPrint} kind={tab === "sku" ? "sku" : "location"} />
      </div>
    </>
  );
}

// Sheet specs match Uline laser-label stock so printed pages line up with the
// physical templates referenced by the customer.
// SKU  → Uline S-5042 (Avery 5160): 2-5/8" x 1", 3 col × 10 row, 30/sheet
// Loc  → Uline S-3845 (Avery 5163): 4" x 2",     2 col × 5  row, 10/sheet
const SHEET_SPECS = {
  sku: {
    perPage: 30,
    cols: 3,
    rows: 10,
    labelW: "2.625in",
    labelH: "1in",
    pageMarginTop: "0.5in",
    pageMarginLeft: "0.1875in",
    colGap: "0.125in",
    rowGap: "0in",
    barcodeHeight: 36,
    barcodeWidth: 1.4,
    fontSize: 9,
  },
  location: {
    perPage: 10,
    cols: 2,
    rows: 5,
    labelW: "4in",
    labelH: "2in",
    pageMarginTop: "0.5in",
    pageMarginLeft: "0.156in",
    colGap: "0in",
    rowGap: "0in",
    barcodeHeight: 80,
    barcodeWidth: 2.4,
    fontSize: 14,
  },
} as const;

function LabelSheet({
  labels,
  kind,
  compact = false,
}: {
  labels: { value: string; label: string; sub?: string }[];
  kind: "sku" | "location";
  compact?: boolean;
}) {
  const spec = SHEET_SPECS[kind];
  // Compact preview shows only filled cells; print mode chunks into full pages.
  const pages: typeof labels[] = compact
    ? [labels]
    : (() => {
        const out: typeof labels[] = [];
        for (let i = 0; i < labels.length; i += spec.perPage) {
          out.push(labels.slice(i, i + spec.perPage));
        }
        return out;
      })();

  return (
    <>
      <style>{`
        @media print {
          @page { size: letter; margin: 0; }
          html, body { background: white !important; }
          body * { visibility: hidden !important; }
          .print-sheet-root, .print-sheet-root * { visibility: visible !important; }
          .print-sheet-root {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 8.5in !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
        .label-sheet-page {
          box-sizing: border-box;
          background: white;
        }
        .label-sheet-page.full {
          width: 8.5in;
          height: 11in;
          padding-top: ${spec.pageMarginTop};
          padding-left: ${spec.pageMarginLeft};
          page-break-after: always;
          break-after: page;
        }
        .label-sheet-page.full:last-child {
          page-break-after: auto;
          break-after: auto;
        }
        .label-sheet-page.compact {
          padding: 0;
          width: max-content;
        }
        .label-sheet-grid {
          display: grid;
          grid-template-columns: repeat(${spec.cols}, ${spec.labelW});
          grid-auto-rows: ${spec.labelH};
          column-gap: ${spec.colGap};
          row-gap: ${spec.rowGap};
        }
        .label-cell {
          width: ${spec.labelW};
          height: ${spec.labelH};
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          box-sizing: border-box;
          padding: 0.06in 0.08in;
          color: black;
          background: white;
          break-inside: avoid;
        }
        @media screen {
          .label-cell { outline: 1px dashed rgba(0,0,0,0.25); }
          .label-sheet-page.full { box-shadow: 0 1px 3px rgba(0,0,0,0.1); margin: 0 auto 12px; }
        }
      `}</style>
      {pages.map((page, pi) => (
        <div key={pi} className={`label-sheet-page ${compact ? "compact" : "full"}`}>
          <div className="label-sheet-grid">
            {page.map((l, i) => (
              <div key={i} className="label-cell">
                <Barcode
                  value={l.value}
                  height={spec.barcodeHeight}
                  width={spec.barcodeWidth}
                  fontSize={spec.fontSize}
                />
                <div
                  className="font-mono font-medium leading-tight text-center truncate max-w-full"
                  style={{ fontSize: spec.fontSize }}
                >
                  {l.label}
                </div>
                {l.sub && (
                  <div
                    className="leading-tight text-center truncate max-w-full"
                    style={{ fontSize: Math.max(8, spec.fontSize - 2), opacity: 0.7 }}
                  >
                    {l.sub}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}